<script setup lang="ts">
import { avatarText } from '@core/utils/formatters'
import { take, words } from 'lodash'
import type { ChangeRequestSubtask, SubtaskStatusOption, SubtaskTypeOption, ChangeRequestPriority } from '@/models/changeRequest'
import { useSnackbarStore } from '@/store/snackbar'
import type { VForm } from 'vuetify/components'
import { requiredValidator } from '@validators'
import { $duplicateChangeRequestSubtask } from '@/api/changeRequests'
import { isDateOverdue } from '@/helpers/utils/isDateOverdue'
import { getCleanFilenameFromUrl } from '@/helpers/utils/downloadAllFilesUsingLinks'
import { getFileIcon } from '@/helpers/utils/files/getFileIcon'
import { usePriority } from '@/helpers/composables/usePriority'

interface IProps {
  requestSubtask?: ChangeRequestSubtask | null,
  showSelectUsersDialog?: boolean,
  changeRequestLoading?: boolean,
  editSubtaskId?: number | null,
  subtaskStatusOptions?: SubtaskStatusOption[],
  subtaskTypeOptions?: SubtaskTypeOption[],
  subtaskPriorityOptions?: ChangeRequestPriority[],
  hideDeleteBtn?: boolean,
  canCreate?: boolean,
  hideDuplicateBtn?: boolean,
}

const props = withDefaults(defineProps<IProps>(), {
  requestSubtask: null,
  showSelectUsersDialog: false,
  changeRequestLoading: false,
  editSubtaskId: null,
  subtaskStatusOptions: () => ([]),
  subtaskPriorityOptions: () => ([]),
  hideDeleteBtn: false,
  canCreate: false,
  hideDuplicateBtn: false,
})

interface Emit {
  (e: 'update:requestSubtask', value:  ChangeRequestSubtask | null): void
  (e: 'update:showSelectUsersDialog', value:  boolean): void
  (e: 'update:editSubtaskId', value:  number | null): void
  (e: 'onCreateChangeRequestSubtask'): void
  (e: 'onUpdateChangeRequestSubtask'): void
  (e: 'onDeleteChangeRequestSubtask'): void
  (e: 'onDuplicateChangeRequestSubtask'): void
  (e: 'onAddChangeRequestSubtaskComment'): void
}

const emit = defineEmits<Emit>()

// Inject from Change Request View page (change-requests/[id]/index.vue) - changeRequestProjectId prop
const changeRequestProjectId = inject<number | null | undefined>('changeRequestProjectId', null)

const refVForm = ref<VForm>()

const {
  getPriorityColor
} = usePriority()

const duplicateChangeRequestSubtaskLoading = ref<boolean>(false)

const showChangeRequestSubtaskCommentsDialog = ref<boolean>(false)

const selectedFileAttachmentId = ref<number | null>(null)
const showFileReviewDialog = ref<boolean>(false)

const snackbarStore = useSnackbarStore()

const computedSubtask = computed({
  // getter
  get: () => props.requestSubtask || {},
  // setter
  set: (newRequestSubtask: ChangeRequestSubtask | null) => {
    emit('update:requestSubtask', newRequestSubtask) // Emit update to parent when the local Subtask is changed
  }
})

const canManageRecord = computed(() => props.requestSubtask?.id
  ? props.requestSubtask.has_ordinary_access === true
  : props.canCreate === true)

const isViewSubtask = computed(() => {
  if (!canManageRecord.value) return true
  if (props.requestSubtask?.id) {
    return props.editSubtaskId !== props.requestSubtask?.id
  } else return false
})

const deleteFile = (fileId: number) => {
  if (!computedSubtask.value) return
  const currentDeletedFiles = computedSubtask.value?.delete_files?.length ? computedSubtask.value?.delete_files : []
  computedSubtask.value.delete_files = [...currentDeletedFiles, fileId] // Track the deleted file ID
  // Update the file_attachments if it exists
  computedSubtask.value.file_attachments =
    computedSubtask.value.file_attachments && Array.isArray(computedSubtask.value.file_attachments)
    ? computedSubtask.value.file_attachments.filter((file: any) => file?.id !== fileId)
    : []
}

// Remove assignedToUser from the list
const removeAssignedToUser = (assignedToUserKey: number) => {
  if (!computedSubtask.value?.assigned_to_users_info?.length) return
  computedSubtask.value.assigned_to_users_info.splice(assignedToUserKey, 1)
}

const duplicateChangeRequestSubtask = async (subtaskId: number) => {
  if (!canManageRecord.value) return
  if (!subtaskId) return

  duplicateChangeRequestSubtaskLoading.value = true
  try {
    await $duplicateChangeRequestSubtask({ id: subtaskId })

    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Change Request Subtask duplicated successfully!'
    })

    emit('onDuplicateChangeRequestSubtask')
  } catch (e: any) {
    console.log(e)
  } finally {
    duplicateChangeRequestSubtaskLoading.value = false
  }
}

const onSubmit = async () => {
  if (!canManageRecord.value) return
  const isValid = await refVForm.value?.validate()
  if (!isValid || !isValid.valid) {
    return
  }

  props.requestSubtask?.id ? emit('onUpdateChangeRequestSubtask') : emit('onCreateChangeRequestSubtask')
}

const handleFileClick = (file: any) => {
  selectedFileAttachmentId.value = file?.id || null
  showFileReviewDialog.value = true
}
</script>

<template>
  <div class="change-request-subtask-wrapper">
    <ChangeRequestObjectCommentsDialog
      v-model="showChangeRequestSubtaskCommentsDialog"
      :change-request-subtask-id="computedSubtask?.id"
      @onAddComment="emit('onAddChangeRequestSubtaskComment')"
    />
    <CrFileReviewDialog
      v-model="showFileReviewDialog"
      :cr-item-type="'crSubtask'"
      :file-attachment-id="selectedFileAttachmentId"
    />
    <h4 class="px-1 pt-2 pb-1 d-flex align-center flex-wrap">
      {{ requestSubtask?.id ? `Subtask ${requestSubtask?.code}` : 'Add a new subtask' }}
      <VBtn
        v-if="isViewSubtask && canManageRecord"
        size=""
        class="pa-1 ml-1"
        :disabled="!!editSubtaskId"
        @click="emit('update:editSubtaskId', requestSubtask?.id)"
      >
        <VIcon icon="tabler-pencil" />
      </VBtn>
      <VBtn
        v-if="isViewSubtask && canManageRecord && !hideDuplicateBtn"
        size=""
        class="pa-1 position-relative ml-1 ml-sm-auto"
        :disabled="!!duplicateChangeRequestSubtaskLoading"
        @click="duplicateChangeRequestSubtask(requestSubtask?.id)"
      >
        <VOverlay
          :model-value="!!duplicateChangeRequestSubtaskLoading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            size="16"
            width="3"
            indeterminate
            color="white"
          />
        </VOverlay>
        <VIcon icon="tabler-copy" />
        <VMenu
          activator="parent"
          location="top center"
          open-on-hover
          open-delay="100"
          close-delay="0"
          offset="5"
          class="default-tooltip"
        >
          <span class="px-2">
            Duplicate Subtask
          </span>
        </VMenu>
      </VBtn>
    </h4>
    <VForm
      ref="refVForm"
      @submit.prevent="onSubmit"
    >
      <VTable class="change-request-subtask-table">
        <tbody>
          <tr>
            <td class="px-1 py-1">
              <span>
                Subtask Type
              </span>
              <span v-if="!isViewSubtask" class="text-primary ml-1">*</span>
            </td>
            <td class="px-1 py-1">
              <span v-if="isViewSubtask">
                {{ requestSubtask?.subtask_type_display }}
              </span>
              <VSelect
                v-else
                v-model="computedSubtask.subtask_type"
                :disabled="!!changeRequestLoading"
                :items="subtaskTypeOptions"
                item-title="display_name"
                item-value="value"
                label="Subtask Type"
                :rules="[requiredValidator]"
              />
            </td>
          </tr>
          <tr>
            <td class="px-1 pt-2 pb-1">
              <span>
                Description
              </span>
            </td>
            <td class="px-1 pt-2 pb-1">
               <div
                v-if="isViewSubtask  && requestSubtask?.description"
                class="border rounded px-2 pt-2"
                style="background-color: rgba(var(--v-theme-on-surface), 0.05) !important;"
              >
                <p
                  v-for="(line, index) in requestSubtask && requestSubtask.description ? requestSubtask.description.split('\n') : []"
                  :key="index"
                  class="mb-2"
                >
                  {{ line }}
                </p>
              </div>
              <span v-else-if="isViewSubtask && !requestSubtask?.description"/>
              <VTextarea
                v-else
                v-model="computedSubtask.description"
                :disabled="!!changeRequestLoading"
                name="Description"
                label="Description"
                type="text"
                rows="2"
              />
            </td>
          </tr>
          <tr>
            <td class="pa-1">
              <span>
                Assigned To
              </span>
            </td>
            <td class="pa-1">
              <VAvatar
                v-for="(assignedToUser, assignedToUserKey) in computedSubtask?.assigned_to_users_info"
                :key="assignedToUserKey"
                rounded
                :size="25"
                color="primary"
                variant="tonal"
                class="ma-1 cursor-pointer"
              >
                <span
                  class="text-sm-center font-weight-medium"
                >
                  {{ avatarText(take(words(assignedToUser?.display_name), 2).join(' ')) }}
                </span>
                <VMenu
                  activator="parent"
                  location="top center"
                  open-on-click
                  close-on-content-click
                  close-on-back
                  open-delay="100"
                  close-delay="0"
                  offset="5"
                  class="default-tooltip"
                >
                  <div class="d-flex align-center justify-center px-2">
                    <span>{{ assignedToUser?.display_name }}</span>
                    <VBtn
                      v-if="!isViewSubtask"
                      size=""
                      class="pa-1"
                      color="light"
                      :disabled="!!changeRequestLoading"
                      @click="removeAssignedToUser(assignedToUserKey)"
                    >
                      <VIcon color="primary" icon="tabler-trash" />
                      <VMenu
                        activator="parent"
                        location="top center"
                        open-on-hover
                        open-delay="100"
                        close-delay="0"
                        offset="1"
                        class="default-tooltip"
                      >
                        <span class="px-2">
                          Delete
                        </span>
                      </VMenu>
                    </VBtn>
                  </div>
                </VMenu>
              </VAvatar>
              <VBtn
                v-if="!isViewSubtask"
                size=""
                color="light"
                class="border px-1 ma-1"
                :disabled="!!changeRequestLoading"
                @click="emit('update:showSelectUsersDialog', true)"
              >
                <VIcon
                  size="22"
                  icon="mdi-plus"
                />
              </VBtn>
            </td>
          </tr>
          <tr>
            <td class="px-1 pt-2 pb-1">
              <span>
                Supplier
              </span>
            </td>
            <td class="px-1 pt-2 pb-1">
              <span v-if="isViewSubtask">
                <!-- Limited CR Subtask Suppliers up to 1 -->
                {{ requestSubtask?.suppliers_info?.[0]?.supplier_name || '' }}
              </span>
              <SupplierSelect
                v-else
                :model-value="computedSubtask?.suppliers_info?.[0] || null"
                :disabled="!!changeRequestLoading"
                :label="'Supplier'"
                :cr-suppliers-only="true"
                :cr-id="computedSubtask.change_request"
                @update:modelValue="
                  newValue => newValue
                    ? computedSubtask.suppliers_info = [newValue]
                    : computedSubtask.suppliers_info = []
              "
              />
            </td>
          </tr>
          <tr>
            <td class="px-1 py-1">
              <span>
                Supplier Cost
              </span>
            </td>
            <td class="px-1 py-1">
              <span v-if="isViewSubtask">
                $
                {{ requestSubtask?.supplier_cost }}
              </span>
              <VTextField
                v-else
                v-model="computedSubtask.supplier_cost"
                :disabled="!!changeRequestLoading"
                label="Supplier Cost"
                type="number"
              />
            </td>
          </tr>
          <tr>
            <td class="pa-1">
              <span>
                Status
              </span>
              <span v-if="!isViewSubtask" class="text-primary ml-1">*</span>
            </td>
            <td class="pa-1">
              <span v-if="isViewSubtask">
                {{ requestSubtask?.status_display }}
              </span>
              <VSelect
                v-else
                v-model="computedSubtask.status"
                :disabled="!!changeRequestLoading"
                :items="subtaskStatusOptions"
                item-title="display_name"
                item-value="value"
                label="Status"
                :rules="[requiredValidator]"
              />
            </td>
          </tr>
          <tr>
            <td class="pa-1">
              <span>
                Priority
              </span>
            </td>
            <td class="pa-1">
              <div v-if="isViewSubtask">
                <VChip
                  :color="getPriorityColor(requestSubtask?.priority)"
                >
                  {{ requestSubtask?.priority_display }}
                </VChip>
              </div>
              <VSelect
                v-else
                v-model="computedSubtask.priority"
                :disabled="!!changeRequestLoading"
                :items="subtaskPriorityOptions"
                item-title="display_name"
                item-value="value"
                label="Priority"
              />
            </td>
          </tr>
          <tr>
            <td class="pa-1">
              <span>
                Due Date
              </span>
            </td>
            <td class="pa-1">
               <span
                 v-if="isViewSubtask"
                 :class="{ 'text-primary': isDateOverdue(requestSubtask?.due_date) }"
               >
                {{ requestSubtask?.due_date }}
              </span>
              <!-- Use separate disabled AppDateTimePicker due to disabled property limitations -->
              <AppDateTimePicker
                v-else-if="!!changeRequestLoading"
                :model-value="computedSubtask?.due_date"
                :config="{ dateFormat: 'Y-m-d' }"
                placeholder="Due Date"
                disabled
              />
              <!-- /Use separate disabled AppDateTimePicker due to disabled property limitations -->
              <AppDateTimePicker
                v-else
                v-model="computedSubtask.due_date"
                :config="{ dateFormat: 'Y-m-d' }"
                clearable
                placeholder="Due Date"
              />
            </td>
          </tr>
          <tr>
            <td class="px-1 pt-1 pb-2">
              <span>
                Notes
              </span>
            </td>
            <td class="px-1 pt-1 pb-2">
              <VChip
                size="small"
                :color="computedSubtask?.comments_count ? 'primary' : 'default'"
                variant="outlined"
              >
                {{ computedSubtask?.comments_count || 0 }}
              </VChip>

              <VBtn
                v-if="isViewSubtask && computedSubtask?.id"
                size=""
                class="pa-1 ml-1"
                @click="showChangeRequestSubtaskCommentsDialog = true"
              >
                <VIcon icon="tabler-message" />
                <VMenu
                  activator="parent"
                  location="top center"
                  open-on-hover
                  open-delay="100"
                  close-delay="0"
                  offset="5"
                  class="default-tooltip"
                >
                  <span class="px-2">
                    View/Add Notes
                  </span>
                </VMenu>
              </VBtn>
            </td>
          </tr>
          <tr>
            <td class="px-1 pb-2 pt-1">
              <span>
                Files
              </span>
            </td>
            <td class="px-1 pb-2 pt-1">
              <VFileInput
                v-if="!isViewSubtask"
                v-model="computedSubtask.files"
                :disabled="!!changeRequestLoading"
                multiple
                label="Select files..."
              />
              <div v-if="requestSubtask?.id" class="file-container mt-1">
                <div v-for="file in requestSubtask?.file_attachments" :key="file.id" class="file-item">
                  <a
                    v-if="file.file"
                    :href="file.file"
                    target="_blank"
                    class="cursor-pointer d-inline-flex align-center"
                    @click.prevent="handleFileClick(file)"
                  >
                    <VIcon
                      :icon="getFileIcon(file.file)"
                      class="mr-1 text-primary"
                    />
                    {{ getCleanFilenameFromUrl(file.file) }}
                  </a>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </VTable>
      <div
        v-if="!isViewSubtask"
        class="d-flex align-center justify-space-between"
      >
        <div>
          <VBtn
            v-if="!requestSubtask?.id"
            class="ma-1"
            size="small"
            color="success"
            :disabled="!!changeRequestLoading"
            type="submit"
          >
            <VProgressCircular
              v-if="!!changeRequestLoading"
              class="mr-1"
              size="16"
              width="2"
              indeterminate
              color="white"
            />
            Add
          </VBtn>
          <VBtn
            class="ma-1"
            size="small"
            color="secondary"
            :disabled="!!changeRequestLoading"
            @click="emit('update:editSubtaskId', null); computedSubtask = null;"
          >
            Cancel
          </VBtn>
          <VBtn
            v-if="requestSubtask?.id && !hideDeleteBtn"
            class="my-1 mr-1"
            size="small"
            color="primary"
            :disabled="!!changeRequestLoading"
            @click="emit('onDeleteChangeRequestSubtask', null)"
          >
            Delete
          </VBtn>
        </div>
        <VBtn
          v-if="requestSubtask?.id"
          class="my-1 mr-1 ml-auto"
          size="small"
          color="success"
          :disabled="!!changeRequestLoading"
          type="submit"
        >
          <VProgressCircular
            v-if="!!changeRequestLoading"
            class="mr-1"
            size="16"
            width="2"
            indeterminate
            color="white"
          />
          Update
        </VBtn>
      </div>
    </VForm>
  </div>
</template>

<style lang="scss">
.change-request-subtask-wrapper {
  border: 1px solid #4d77de;
  border-radius: 25px;
  padding: 10px 20px;
  .change-request-subtask-table {
    tr {
      td:first-child {
        width: 30%;
      }
      td {
        height: unset !important;
        border-bottom: none !important;
      }
    }
  }
}
</style>
